import { useState } from "react";
import { ApiService } from "../../../../services/api";
import { useModal } from "../../../../hooks/useModal";
import checkPermission from "../../../../utils/permission";
import Modal from "../../../../components/ui/Modal";

export default function SectionModal({
  lessonId,
  isOpen,
  onClose,
  sectionId = null,
}: {
  lessonId: string;
  isOpen: boolean;
  onClose: () => void;
  sectionId?: string | null;
}) {
  const { showAlert } = useModal();
  const canCreate = checkPermission("lesson_section_add");
  const canUpdate = checkPermission("lesson_section_update");
  const [section, setSection] = useState({
    title: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!section.title.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen bölüm başlığını giriniz.",
        type: "error",
      });
      return;
    }

    if (!section.description.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen bölüm açıklamasını giriniz.",
        type: "error",
      });
      return;
    }

    setLoading(true);
    try {
      if (!sectionId && !canCreate) {
        await showAlert({
          title: "Yetki Hatası",
          message: "Bölüm ekleme yetkiniz yok.",
          type: "error",
        });
        return;
      }
      if (sectionId && !canUpdate) {
        await showAlert({
          title: "Yetki Hatası",
          message: "Bölüm güncelleme yetkiniz yok.",
          type: "error",
        });
        return;
      }
      const request = {
        title: section.title.trim(),
        description: section.description.trim(),
        metadata: {},
      };

      if (sectionId) {
        await ApiService.updateLessonSection(lessonId, sectionId, request);
      } else {
        await ApiService.createLessonSection(lessonId, request);
      }

      setSection({ title: "", description: "" });
      onClose();
    } catch (err) {
      await showAlert({
        title: "Hata",
        message: "Bölüm kaydedilirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSection({ title: "", description: "" });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={sectionId ? "Bölümü Düzenle" : "Yeni Bölüm Ekle"} size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bölüm Başlığı <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Örn: Bölüm 1: Giriş"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
            value={section.title}
            onChange={(e) => setSection({ ...section, title: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Açıklama <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            placeholder="Bu bölümde ele alınacak konular hakkında kısa bir açıklama..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none"
            value={section.description}
            onChange={(e) => setSection({ ...section, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            disabled={loading}>
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={
              loading || !section.title.trim() || !section.description.trim() || (!sectionId && !canCreate) || (!!sectionId && !canUpdate)
            }
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium">
            {loading ? "Kaydediliyor..." : sectionId ? "Güncelle" : "Kaydet"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
